import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useCallback, useState } from 'react'
import { Modal } from 'react-bootstrap'
import CloseConfirmationModal, { useCloseConfirmationModal } from '../../../../../components/CloseConfirmationModal'
import { usePolicyTemplatesContext } from '../../../../../context/PolicyTemplatesContext'
import TemplateGroupCreate from './TemplateGroupCreate'
import TemplateGroupEdit from './TemplateGroupEdit'
import TemplateGroupView from './TemplateGroupView'

export const MODAL_STATES = {
  VIEW: 'VIEW',
  EDIT: 'EDIT',
  CREATE: 'CREATE',
}

const propTypes = {
  /**
   * If true, shows the modal
   */
  show: PropTypes.bool,
  /**
   * Callback called when modal is going to be hidden
   */
  onHide: PropTypes.func.isRequired,
  /**
   * map of form options that are non tenant specific
   */
  globalFormOptionsMap: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  /**
   * modal state when opened
   */
  modalState: PropTypes.string,
  /**
   * Selected template group object
   */
  selectedTemplateGroup: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  /**
   * Callback to open the modal setting the modal state
   */
  openTemplateGroupModal: PropTypes.func.isRequired,
}

function TemplateGroupModal(props) {
  const {
    show = false,
    onHide,
    globalFormOptionsMap = {},
    modalState = MODAL_STATES.VIEW,
    selectedTemplateGroup = {},
    openTemplateGroupModal,
  } = props ?? {}

  const {
    policyTemplates,
    loadingPolicyTemplateGroup,
    errorPolicyTemplateGroup,
    fetchPolicyTemplateGroupList,
    createPolicyTemplateGroup,
    updatePolicyTemplateGroup,
  } = usePolicyTemplatesContext()

  const [hasFormChanged, setHasFormChanged] = useState(false)

  const shouldShowCloseConfirmation = useCallback(
    () => hasFormChanged && (modalState === MODAL_STATES.CREATE || MODAL_STATES.EDIT),
    [hasFormChanged, modalState],
  )

  const { showCloseConfirmationModal, hideCloseConfirmationModal, onHideOverride } = useCloseConfirmationModal({
    onHide,
    shouldShowCloseConfirmation,
  })

  return (
    <>
      <Modal
        size="lg"
        backdrop
        className={clsx('right text-color-default', {
          in: show,
        })}
        show={show}
        onHide={onHideOverride}
      >
        {modalState === MODAL_STATES.CREATE && (
          <TemplateGroupCreate
            onHide={onHideOverride}
            onSuccess={onHide}
            policyTemplates={policyTemplates}
            globalFormOptionsMap={globalFormOptionsMap}
            loadingPolicyTemplateGroup={loadingPolicyTemplateGroup}
            errorPolicyTemplateGroup={errorPolicyTemplateGroup}
            createPolicyTemplateGroup={createPolicyTemplateGroup}
            fetchPolicyTemplateGroupList={fetchPolicyTemplateGroupList}
            setHasFormChanged={setHasFormChanged}
          />
        )}
        {modalState === MODAL_STATES.EDIT && (
          <TemplateGroupEdit
            onHide={onHideOverride}
            onSuccess={onHide}
            policyTemplates={policyTemplates}
            globalFormOptionsMap={globalFormOptionsMap}
            loadingPolicyTemplateGroup={loadingPolicyTemplateGroup}
            errorPolicyTemplateGroup={errorPolicyTemplateGroup}
            updatePolicyTemplateGroup={updatePolicyTemplateGroup}
            fetchPolicyTemplateGroupList={fetchPolicyTemplateGroupList}
            selectedTemplateGroup={selectedTemplateGroup}
            setHasFormChanged={setHasFormChanged}
          />
        )}
        {modalState === MODAL_STATES.VIEW && (
          <TemplateGroupView
            onHide={onHideOverride}
            globalFormOptionsMap={globalFormOptionsMap}
            selectedTemplateGroup={selectedTemplateGroup}
            onEditClick={() => openTemplateGroupModal(selectedTemplateGroup, MODAL_STATES.EDIT)}
          />
        )}
      </Modal>

      <CloseConfirmationModal
        show={showCloseConfirmationModal}
        onHideConfirmation={hideCloseConfirmationModal}
        onHideParentModal={onHide}
      />
    </>
  )
}

TemplateGroupModal.propTypes = propTypes

export default TemplateGroupModal
